import { FilterAndSortOptions, multiTypeSort } from 'src/state/redux/selector/common';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useReducer } from 'react';
import { FullTextSearchQuery, FullTextSearchOutput, TextWithHighlights } from '@alucio/aws-beacon-amplify/src/API'
import {
  fullTextSearch as fullTextSearchQuery,
} from '@alucio/aws-beacon-amplify/src/graphql/queries'
import { generateClient } from 'aws-amplify/api';
import { DocumentORM } from 'src/types/orms'
import {
  DocumentSearchOptions,
  useCanPerformSearch,
  useDocumentSearch,
} from 'src/state/redux/selector/documentSearch/documentSearch';
import { useAppSettings } from 'src/state/context/AppSettings'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import { merge } from 'src/state/redux/document/query'
import debounce from 'lodash/debounce'
import { useAllDocumentsInstance, useAllDocumentVersionMap } from 'src/state/redux/selector/document';
import useIsDebugView from 'src/hooks/useIsDebugView/useIsDebugView';
import { runThrottledFunction, warmupSearchSilently } from 'src/utils/searchWarmup';
import { filterSearchResults, getMinimumRelativeSearchScore } from 'src/utils/filterSearchResults';
import { AISearchFlowId } from 'src/types/aiSearchFlows';
import type { AISearchRequestSnapshot } from 'src/utils/aiSearchHistory';

const SESSION_STORAGE_AI_SEARCH_KEY = 'beacon.search.isAiSearch';

export interface AIScientificSummaryCitation {
  source_name?: string;
  document_version_id: string;
  page_number: string | number;
  exact_quoted_text: string;
}

export interface AIScientificSummaryParagraph {
  narrative_text: string;
  citations: AIScientificSummaryCitation[];
}

export interface AIMessagingDocuments {
  included: boolean;
  relevance_assessment?: string;
  narrative_text?: string;
  citations: AIScientificSummaryCitation[];
}

export interface AIScientificSummary {
  scientific_summary: {
    header: {
      title: string;
      disclaimer: string;
      original_question: string;
    };
    body_paragraphs: AIScientificSummaryParagraph[];
    answer_found?: boolean;
    messaging_documents?: AIMessagingDocuments;
  };
}

export interface ReferenceMetadata {
  id: string;
  title: string;
  page_number?: string;
  text?: string;
  quotedText?: string;
  isMessaging?: boolean;
}

export type AISearchSessionData = {
  request?: AISearchRequestSnapshot;
  searchText: string;
  report: AIScientificSummary | null;
  references: ReferenceMetadata[];
  sessionId: string | null;
  loading: boolean;
  statusMessage: string;
  error: string | null;
}

export type AISearchAction =
  | { type: 'QUEUE_SEARCH'; payload: string }
  | { type: 'SET_SEARCH_TEXT'; payload: string }
  | { type: 'INITIATE_SEARCH'; payload: {
      searchText: string; sessionId?: string | null; request?: AISearchRequestSnapshot;
    } }
  | { type: 'UPDATE_STATUS'; payload: string }
  | { type: 'SET_REPORT'; payload: AIScientificSummary }
  | { type: 'SET_REFERENCES'; payload: ReferenceMetadata[] }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET' };

export const aiSearchReducer = (state: AISearchSessionData, action: AISearchAction): AISearchSessionData => {
  switch (action.type) {
    case 'QUEUE_SEARCH':
      return {
        ...state,
        request: undefined,
        searchText: action.payload,
        report: null,
        references: [],
        sessionId: null,
        loading: false,
        statusMessage: '',
        error: null,
      };
    case 'SET_SEARCH_TEXT':
      return { ...state, searchText: action.payload };
    case 'INITIATE_SEARCH':
      return {
        ...state,
        request: action.payload.request,
        searchText: action.payload.searchText,
        sessionId: action.payload.sessionId || null,
        report: null,
        references: [],
        loading: true,
        statusMessage: 'Initiating search...',
        error: null,
      };
    case 'UPDATE_STATUS':
      return { ...state, statusMessage: action.payload };
    case 'SET_REPORT':
      return {
        ...state,
        report: action.payload,
        sessionId: null,
        loading: false,
        statusMessage: '',
        error: null,
      };
    case 'SET_REFERENCES':
      return { ...state, references: action.payload };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        sessionId: null,
        loading: false,
        statusMessage: '',
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'RESET':
      return {
        searchText: '',
        report: null,
        references: [],
        sessionId: null,
        loading: false,
        statusMessage: '',
        error: null,
      };
    default:
      return state;
  }
};

type SearchStatus = 'IDLE' | 'PENDING' | 'RESULTS' | 'EMPTY_RESULTS' | 'FALLBACK';
export type Mode =
  'HEADER' | 'VIEWER_RESULTS' | 'PUBLISHER_RESULTS' | 'FOLDER' | 'CUSTOM' | 'MEETING' | 'NOT_PROVIDED'

export type SearchContextValue = {
  query: FilterAndSortOptions<DocumentORM> | null | undefined
  documentIdSnippetMap: Map<string, documentSnippetType>
  fullTextSearch: ReturnType<typeof debounce<(searchText: string, mode?: Mode) => Promise<void>>>;
  fullTextSearchResult?: FullTextSearchOutput
  searchText: string;
  status: SearchStatus
  resetSearch: () => void;
  isLoadingSearch: boolean;
  onPreviewDocumentFromSearchResult: (documents: DocumentORM[], documentVersionId: string) => void,
  searchTiming: {
    serverTime: number | null | undefined;
    clientTime: number | null | undefined;
    totalDuration: number | null | undefined;
    processedQuery: string | null | undefined;
    errorMessage: string | null;
  };
  isAiSearch: boolean;
  setIsAiSearch: (isAiSearch: boolean) => void;
  aiSearchSessionData: AISearchSessionData;
  dispatchAiSearch: React.Dispatch<AISearchAction>;
  aiSearchFlowId: AISearchFlowId | null;
  setAISearchFlowId: (flowId: AISearchFlowId) => void;
  allDocVersions: ReturnType<typeof useAllDocumentVersionMap>;
}

const SearchContext = createContext<SearchContextValue>({
  query: null,
  documentIdSnippetMap: new Map(),
  fullTextSearch: debounce(async (_: string) => undefined, 500),
  onPreviewDocumentFromSearchResult: () => {},
  searchText: '',
  status: 'IDLE',
  resetSearch: () => undefined,
  isLoadingSearch: false,
  searchTiming: {
    serverTime: null,
    clientTime: null,
    totalDuration: null,
    processedQuery: null,
    errorMessage: null,
  },
  isAiSearch: false,
  setIsAiSearch: () => undefined,
  aiSearchSessionData: {
    searchText: '',
    report: null,
    references: [],
    sessionId: null,
    loading: false,
    statusMessage: '',
    error: null,
  },
  dispatchAiSearch: () => undefined,
  aiSearchFlowId: null,
  setAISearchFlowId: () => undefined,
  allDocVersions: {},
})

interface ProviderProps {
  searchText?: string,
  children: React.ReactElement
}

export type documentSnippetType = {
  title: TextWithHighlights,
  documentExcerpt: TextWithHighlights
  resultId: string
  queryId: string
  index: number
  pageNumber: number
}

const constructQuery = (idMap: Map<string, documentSnippetType>) => {
  const newFilterAndSortOptions: FilterAndSortOptions<DocumentORM> = {
    filter: {
      model: {
        id: (id) => idMap.has(id),
      },
    },
    sort: [
      {
        model: {
          id: (a, b) => {
            return multiTypeSort(idMap.get(a)?.index, idMap.get(b)?.index, { dir: 'asc' })
          },
        },
      },
    ],
  }
  return newFilterAndSortOptions
}

const currentPageQuery = { exact: false }
// todo: flexible filtering
const statusFilters = {
  PUBLISHER: ['PUBLISHED', 'NOT_PUBLISHED', 'ARCHIVED', 'REVOKED'],
  VIEWER: ['PUBLISHED'],
}

const recordSearchEvent = (mode: Mode) => {
  // adding this check since we don't want to record events as they type in the search dropdown
  // only when they hit enter
  if (mode !== 'HEADER') {
    analytics?.track('SEARCH_DOCUMENT_SEARCH', {
      category: 'SEARCH',
      action: 'DOCUMENT_SEARCH',
      context: mode,
    });
  }
}

/**
 * Tracks document search analytics.
 *
 * This function sends analytics data related to document searches. It includes
 * both server and client search times, the duration of the search, the mode of
 * the search, and the search text itself.
 *
 * @param {number | null | undefined} searchServerTime - "took" attribute from Open Search result, in JSON number format.
 * @param {number | null | undefined} searchClientTime - Time for end to end response time openSearch, in JSON number format.
 * @param {number} duration - Total end to end time measured by the client.
 * @param {Mode} mode - The mode of the search.
 * @param {string} searchText - The text used for the search.
 */
const trackDocumentSearchAnalytics = (
  searchServerTime: number | null | undefined,
  searchClientTime: number | null | undefined,
  duration: number,
  searchText: string,
) => {
  try {
    const totalTime = duration;
    analytics?.track('SEARCH_REQUEST', {
      category: 'SEARCH',
      action: 'REQUEST',
      searchServerTime: searchServerTime,
      searchClientTime: searchClientTime,
      totalTime,
      searchText,
    });
  } catch (error) {
    console.error('Failed to track document search analytics:', error);
  }
}

const useNameOfSearchAdapter = () => {
  const enableHybridSearch_V2_0 = useFeatureFlags('enableHybridSearch_V2_0');
  const enableHybridSearch_V2_1 = useFeatureFlags('enableHybridSearch_V2_1');
  const enableHybridSearch_V2_2 = useFeatureFlags('enableHybridSearch_V2_2');

  // If V2.1 flag is enabled, it takes precedence
  if (enableHybridSearch_V2_1) {
    return 'Hybrid_Search_v2_1';
  }

  // If V2.0 flag is enabled
  if (enableHybridSearch_V2_0) {
    return 'Hybrid_Search_v2_0';
  }

  // If V2.2 flag is enabled
  if (enableHybridSearch_V2_2) {
    return 'Hybrid_Search_v2_2';
  }

  // Default case: when both flags are false or undefined
  return 'Hybrid_Search_v1';
}

const DocumentSearchProvider: React.FC<ProviderProps> = ({ children }) => {
  const [query, setQuery] = useState<FilterAndSortOptions<DocumentORM> | null>(null);
  const [lunrText, setLunrText] = useState<string>('');
  const [fullTextSearchResult, setFullTextSearchResult] = useState<FullTextSearchOutput | undefined>();
  const [documentIdSnippetMap, setDocumentIdSnippetMap] =
    useState<Map<string, documentSnippetType>>(new Map());
  const [localText, setLocalText] = useState('');
  const { isOnline } = useAppSettings();
  const canPerformSearch = useCanPerformSearch()
  const searchAdapterName = useNameOfSearchAdapter();
  const route = useCurrentPage(currentPageQuery)
  const [status, setStatus] = useState<SearchStatus>('IDLE')
  const [isAiSearch, setIsAiSearchState] = useState<boolean>(() => {
    const saved = sessionStorage.getItem(SESSION_STORAGE_AI_SEARCH_KEY);
    return saved === 'true';
  })

  const setIsAiSearch = useCallback((value: boolean) => {
    setIsAiSearchState(value);
    sessionStorage.setItem(SESSION_STORAGE_AI_SEARCH_KEY, String(value));
  }, []);

  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const isDebugRoute = useIsDebugView()
  const isAiSearchEnabled = useFeatureFlags('BEAC_7644_AI_search', 'BEAC_8091_AI_search_view_only');
  const isAiSearchActive = (!isOnline || isPublisherRoute ||
    !(isAiSearchEnabled.BEAC_7644_AI_search || isAiSearchEnabled.BEAC_8091_AI_search_view_only)) ? false : isAiSearch;

  const [aiSearchSessionData, dispatchAiSearch] = useReducer(aiSearchReducer, {
    searchText: '',
    report: null,
    references: [],
    sessionId: null,
    loading: false,
    statusMessage: '',
    error: null,
  });
  const allDocVersions = useAllDocumentVersionMap();
  const [aiSearchFlowId, setSelectedAISearchFlowId] = useState<AISearchFlowId | null>(null);
  const setAISearchFlowId = useCallback((flowId: AISearchFlowId) => {
    if (aiSearchSessionData.loading || flowId === aiSearchFlowId) return;
    setSelectedAISearchFlowId(flowId);
    dispatchAiSearch({ type: 'RESET' });
  }, [aiSearchFlowId, aiSearchSessionData.loading]);
  const [searchTiming, setSearchTiming] = useState<{
    serverTime: number | null | undefined;
    clientTime: number | null | undefined;
    totalDuration: number | null | undefined;
    processedQuery: string | null | undefined;
    errorMessage: string | null;
  }>({
    serverTime: null,
    clientTime: null,
    totalDuration: null,
    processedQuery: null,
    errorMessage: null,
  });

  const onPreviewDocumentFromSearchResult = (documents: DocumentORM[], documentVersionId: string): void => {
    if (fullTextSearchResult) {
      const documentId = documentVersionId.split('_')[0];
      const item = fullTextSearchResult.items.find((item) => item.documentId === documentId);
      const itemIndexInList = documents.findIndex((documentORM) => documentORM.model.id === item?.documentId);

      if (item) {
        analytics?.track('SEARCH_RESULT_SELECTED', {
          category: 'SEARCH',
          score: item.score,
          rank: item.rank,
          rank_in_filtered_results: itemIndexInList + 1,
          orig_rank: item.originalRank,
          orig_score: item.originalScore,
          documentVersionId,
          result_type: item.resultId === 'lexical_result'
            ? 'lexical'
            : (item.resultId === 'semantic_result' ? 'semantic' : undefined),
          query: fullTextSearchResult.processedQuery,
        });
      }
    }
  };

  const memoSearchOpts: DocumentSearchOptions = useMemo(() => {
    return {
      text: lunrText,
      fields: ['title'],
      isPublisher: isPublisherRoute,
    }
  }, [lunrText, isPublisherRoute])

  const lunrResults = useDocumentSearch(memoSearchOpts);

  // Token used to handle "cancellation" of outdated search requests
  // Since Amplify's API.graphql doesn't provide a way to cancel in-flight requests,
  // we use this token to ignore results from outdated searches
  // TODO: Replace this pattern with proper request cancellation once Amplify supports it
  // See: https://github.com/aws-amplify/amplify-js/issues/989
  const searchTokenRef = React.useRef<number>(0);

  // Reference to store the last search text to avoid duplicate searches
  const lastSearchTextRef = React.useRef<string>('');

  // Reference to store the last search results to return them on duplicate searches
  const lastSearchResultsRef = React.useRef<{
    documentIdSnippetMap: Map<string, documentSnippetType>;
    fullTextSearchResult: FullTextSearchOutput | undefined;
    query: FilterAndSortOptions<DocumentORM> | null;
    searchTiming: {
      serverTime: number | null | undefined;
      clientTime: number | null | undefined;
      totalDuration: number | null | undefined;
      processedQuery: string | null | undefined;
      errorMessage: string | null;
    };
    status: SearchStatus;
    minimumRelativeSearchScore: number;
  } | null>(null);

  const fullTextSearch = useCallback(
    async (searchText: string, mode?: Mode) => {
      if (isAiSearchActive) {
        setLocalText(searchText);
        return;
      }

      const minimumRelativeSearchScore = getMinimumRelativeSearchScore();

      // Skip search if it's exactly the same as the previous search
      if (
        searchText.trim() === lastSearchTextRef.current.trim() &&
        lastSearchResultsRef.current &&
        lastSearchResultsRef.current.minimumRelativeSearchScore === minimumRelativeSearchScore
      ) {
        setDocumentIdSnippetMap(lastSearchResultsRef.current.documentIdSnippetMap);
        setFullTextSearchResult(lastSearchResultsRef.current.fullTextSearchResult);
        setQuery(lastSearchResultsRef.current.query);
        setSearchTiming(lastSearchResultsRef.current.searchTiming);
        setStatus(lastSearchResultsRef.current.status);
        setLocalText(searchText);
        return;
      }

      // Check if there's already a search in progress with the same text
      if (
        searchText === lastSearchTextRef.current &&
        lastSearchResultsRef.current?.minimumRelativeSearchScore === minimumRelativeSearchScore
      ) {
        return;
      }

      lastSearchTextRef.current = searchText;

      setLocalText(searchText);
      if (isOnline && canPerformSearch(searchText)) {
        setStatus('PENDING')
        recordSearchEvent(mode || 'NOT_PROVIDED');

        // Increment token for new search - higher numbers represent more recent searches
        // This ensures only the latest search results are processed
        const currentToken = ++searchTokenRef.current;
        const name = localStorage.getItem('overrideLexicalAdapterName') || searchAdapterName

        try {
          const searchExpression = fullTextSearchQuery;

          const startTime = performance.now();
          const appsyncClient = generateClient();
          const { data } = await appsyncClient.graphql({
            query: searchExpression,
            variables: {
              queryText: searchText,
              filter: {
                statuses: statusFilters[(isPublisherRoute || isDebugRoute) ? 'PUBLISHER' : 'VIEWER'],
              },
              name,
            },
          });

          // If this search is no longer the most recent one, discard its results
          // This prevents race conditions where older search results could override newer ones
          if (currentToken !== searchTokenRef.current) {
            return;
          }

          const endTime = performance.now();
          const duration = (endTime - startTime) / 1000; // Convert milliseconds to seconds

          const documentResultMap: Map<string, documentSnippetType> = new Map();
          const dataResult = (data as FullTextSearchQuery).fullTextSearch;

          // Update search timing information
          setSearchTiming({
            serverTime: dataResult?.searchServerTime,
            clientTime: dataResult?.searchClientTime,
            totalDuration: duration,
            processedQuery: dataResult?.processedQuery,
            errorMessage: null,
          });

          filterSearchResults(dataResult?.items ?? [], minimumRelativeSearchScore).forEach((curr, index) => {
            if (curr?.documentId) {
              const documentId = curr.documentId
              documentResultMap.set(documentId, {
                title: curr.title,
                documentExcerpt: curr.documentExcerpt,
                resultId: curr.resultId,
                queryId: curr.queryId,
                pageNumber: curr.pageNumber || 1,
                index: index,
              });
            }
          });

          // fallback to lunr
          if (documentResultMap.size === 0) {
            setLunrText(searchText);
            setStatus('FALLBACK');
          }
          else {
            const newQuery = constructQuery(documentResultMap);
            const newTiming = {
              serverTime: dataResult?.searchServerTime,
              clientTime: dataResult?.searchClientTime,
              totalDuration: duration,
              processedQuery: dataResult?.processedQuery,
              errorMessage: null,
            };

            setFullTextSearchResult(dataResult!);
            setDocumentIdSnippetMap(documentResultMap);
            setQuery(newQuery);
            setStatus('RESULTS');

            // Store results for potential duplicate searches
            lastSearchResultsRef.current = {
              documentIdSnippetMap: documentResultMap,
              fullTextSearchResult: dataResult!,
              query: newQuery,
              searchTiming: newTiming,
              status: 'RESULTS',
              minimumRelativeSearchScore,
            };

            trackDocumentSearchAnalytics(
              dataResult?.searchServerTime,
              dataResult?.searchClientTime,
              duration,
              searchText,
            );
          }
        }
        catch (err) {
          // fallback to lunr
          if (currentToken === searchTokenRef.current) {
            const errorMessage = err instanceof Error
              ? err.message
              : (err as any)?.errors?.[0]?.message || 'Unknown error occurred';
            setSearchTiming(prev => ({
              ...prev,
              errorMessage,
            }));
            setLunrText(searchText);
            setStatus('FALLBACK');
          }
        }
      }
      else if (canPerformSearch(searchText)) {
        recordSearchEvent(mode || 'NOT_PROVIDED');
        setLunrText(searchText);
        setStatus('FALLBACK');
      }
    },
    [canPerformSearch, isAiSearchActive, isDebugRoute, isOnline, isPublisherRoute, searchAdapterName],
  );

  const debouncedSearch = useCallback(
    debounce(fullTextSearch, 500),
    [fullTextSearch],
  );

  useEffect(
    () => {
      if (status === 'FALLBACK') {
        const map = lunrResults.reduce((acc, curr) => {
          acc.set(curr.model.id, undefined);
          return acc;
        }, new Map());
        const newQuery = constructQuery(map);
        setDocumentIdSnippetMap(map);
        setQuery(newQuery);

        if (map.size === 0) {
          analytics?.track('NO_RESULT', {
            category: 'SEARCH',
            action: 'NO_RESULT',
          });
          setStatus('EMPTY_RESULTS');

          // Store empty results for potential duplicate searches
          lastSearchResultsRef.current = {
            documentIdSnippetMap: map,
            fullTextSearchResult: undefined,
            query: newQuery,
            searchTiming: searchTiming,
            status: 'EMPTY_RESULTS',
            minimumRelativeSearchScore: getMinimumRelativeSearchScore(),
          };
        }
        else {
          setStatus('RESULTS');

          // Store lunr results for potential duplicate searches
          lastSearchResultsRef.current = {
            documentIdSnippetMap: map,
            fullTextSearchResult: undefined,
            query: newQuery,
            searchTiming: searchTiming,
            status: 'RESULTS',
            minimumRelativeSearchScore: getMinimumRelativeSearchScore(),
          };
        }
      }
    },
    [lunrResults, status, searchTiming],
  )

  const resetSearch = useCallback(() => {
    setQuery(null);
    setDocumentIdSnippetMap(new Map());
    setFullTextSearchResult(undefined);
    setLunrText('');
    setLocalText('');
    setStatus('IDLE');
  }, []);

  // Execute dummy search to warm up the lambda when the provider is mounted and user is online
  useEffect(() => {
    if (!isOnline) return;

    const name = localStorage.getItem('overrideLexicalAdapterName') || searchAdapterName;
    runThrottledFunction(() => warmupSearchSilently('__warmup_query_no_results__', name));
  }, [isOnline, searchAdapterName]);

  return (
    <SearchContext.Provider
      value={{
        query,
        documentIdSnippetMap,
        fullTextSearch: debouncedSearch,
        searchText: localText,
        fullTextSearchResult,
        status,
        onPreviewDocumentFromSearchResult,
        resetSearch,
        isLoadingSearch: !(status === 'EMPTY_RESULTS' || status === 'RESULTS'),
        searchTiming,
        isAiSearch: isAiSearchActive,
        setIsAiSearch,
        aiSearchSessionData,
        dispatchAiSearch,
        aiSearchFlowId,
        setAISearchFlowId,
        allDocVersions,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export const useDocumentSearchV2Context = () => useContext(SearchContext)

export const documentSearchV2Factory = (contextIn: typeof SearchContext) => (
  searchText?: string,
  mode?: Mode,
  ...queriesIn: FilterAndSortOptions<DocumentORM>[]
) => {
  const context = useContext(contextIn);
  const { fullTextSearch, isAiSearch, resetSearch } = context;

  useEffect(
    () => {
      if (searchText) {
        fullTextSearch(searchText, mode)
      } else {
        resetSearch()
      }
    },
    [fullTextSearch, isAiSearch, mode, resetSearch, searchText],
  )

  const searchQuery: FilterAndSortOptions<DocumentORM> | undefined = useMemo(
    () => merge(context.query, ...queriesIn),
    [context.query, ...queriesIn],
  )

  // [TODO-4140] - Possibly make this optional somehow or return an already memoized value if the consumer doesn't care about these results
  const docORMResults = useAllDocumentsInstance(searchQuery)

  const value = useMemo(
    () => ({
      ...context,
      searchQuery,
      documentORMSearchResults: docORMResults,
    }),
    [context, searchQuery, docORMResults],
  )

  return value
}

export const useDocumentSearchV2 = documentSearchV2Factory(SearchContext)

export default DocumentSearchProvider;
